// Etiquetas, formatos y clasificación del estado del servicio para la pantalla Promos venta.
import { ApiError } from '@/api/errors';
import { LIFECYCLE_EVENT_LABEL } from '@/shared/patterns/Lifecycle';
import { PromotionsServiceNotConfiguredError } from '../api/promotions.client';
import type {
    SalePromotionLevers,
    SalePromotionLogEntry,
    SalePromotionLogEvent,
} from '../api/promotionsContract';

export type LeverField = keyof SalePromotionLevers;

/** Etiqueta de cada palanca, en el orden en que se muestran. */
export const LEVER_LABEL: Record<LeverField, string> = {
    deviceDiscount: 'Descuento del equipo',
    accessoryDiscount: 'Descuento en accesorios',
    gift: 'Regalo',
    coupon: 'Cupón próxima compra',
    airtime: 'Tiempo aire',
    extendedWarranty: 'Garantía extendida',
    walletBonus: 'Bono al Wallet',
};

export const LEVER_FIELDS = Object.keys(LEVER_LABEL) as readonly LeverField[];

/** Las mismas etiquetas que el CSV del historial que arma retail-promotions-business. */
export const LOG_EVENT_LABEL: Record<SalePromotionLogEvent, string> = {
    CREATE: LIFECYCLE_EVENT_LABEL.CREATE,
    PUBLISH: LIFECYCLE_EVENT_LABEL.PUBLISH,
    UPDATE: LIFECYCLE_EVENT_LABEL.UPDATE,
    PAUSE: LIFECYCLE_EVENT_LABEL.PAUSE,
    RESUME: LIFECYCLE_EVENT_LABEL.RESUME,
    EXPIRE: LIFECYCLE_EVENT_LABEL.EXPIRE,
    ARCHIVE: LIFECYCLE_EVENT_LABEL.ARCHIVE,
};

/** Sin `performedByUserId` el evento es automático. */
export function actorLabel(entry: SalePromotionLogEntry): string {
    if (entry.performedByUserId === null) return 'Sistema';
    return entry.performedByName.trim() ? entry.performedByName : 'Admin VL';
}

export function formatBudgetUsed(budgetUsedBps: number | null): string {
    if (budgetUsedBps === null) return 'Sin tope';
    return `${String(Math.round(budgetUsedBps / 100))}%`;
}

const intFormat = new Intl.NumberFormat('es-MX');

export function formatInt(value: number): string {
    return intFormat.format(value);
}

export type PromotionsAvailability = 'unavailable' | 'forbidden' | 'failed' | 'loading' | 'ready';

export interface PromotionsAvailabilityInput {
    configured: boolean;
    isSuccess: boolean;
    error: unknown;
}

/** Un 403 sin código Fiado viene de API Gateway, que así responde a una ruta que no existe. */
function hasFiadoCode(error: ApiError): boolean {
    return Boolean(error.code) && error.code !== 'UNKNOWN_ERROR';
}

export function resolvePromotionsAvailability({
    configured,
    isSuccess,
    error,
}: PromotionsAvailabilityInput): PromotionsAvailability {
    if (!configured || error instanceof PromotionsServiceNotConfiguredError) return 'unavailable';
    if (error instanceof ApiError) {
        if (error.httpStatus === 404) return 'unavailable';
        if (error.httpStatus === 403) return hasFiadoCode(error) ? 'forbidden' : 'unavailable';
        return 'failed';
    }
    if (error) return 'failed';
    return isSuccess ? 'ready' : 'loading';
}

/** Nunca reintenta sin configurar ni en 4xx; una sola vez en lo demás. */
export function shouldRetryPromotionsQuery(failureCount: number, error: unknown): boolean {
    if (error instanceof PromotionsServiceNotConfiguredError) return false;
    if (error instanceof ApiError && error.httpStatus >= 400 && error.httpStatus < 500)
        return false;
    return failureCount < 1;
}
